import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import FinanceDashboard from './FinanceDashboard';
import PayrollTab from './PayrollTab';
import Invoices from './Invoices';
import { requestsAPI, projectsAPI } from '../services/api';

const tabList = [
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'invoices', label: 'Invoices' },
  { key: 'payments', label: 'Payments' },
  { key: 'payroll', label: 'Payroll' },
  { key: 'expenses', label: 'Expenses' },
  { key: 'requests', label: 'Requests' },
];

const TabContent = ({ activeTab }: { activeTab: string }) => {
  switch (activeTab) {
    case 'dashboard':
      return <FinanceDashboard />;
    case 'invoices':
      return <Invoices />;
    case 'payments':
      return <div className="p-8"><h2 className="text-xl font-bold mb-4">Payments</h2><div className="bg-white rounded-lg shadow p-6">[Payments Module UI]</div></div>;
    case 'payroll':
      return <PayrollTab />;
    case 'expenses':
      return <div className="p-8"><h2 className="text-xl font-bold mb-4">Expenses</h2><div className="bg-white rounded-lg shadow p-6">[Expenses Module UI]</div></div>;
    case 'requests':
      return <RequestsTab />;
    default:
      return null;
  }
};

const RequestsTab: React.FC = () => {
  const [requests, setRequests] = useState<any[]>([]);
  const [projectsMap, setProjectsMap] = useState<{ [key: string]: string }>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectingId, setRejectingId] = useState<string | null>(null);

  useEffect(() => {
    const fetchRequestsAndProjects = async () => {
      setLoading(true);
      try {
        const [requestsRes, projectsRes] = await Promise.all([
          requestsAPI.getAll(),
          projectsAPI.getAll(),
        ]);
        setRequests(requestsRes.data);
        const map: { [key: string]: string } = {};
        projectsRes.data.forEach((p: any) => {
          map[p._id] = p.name;
          if (p.projectId) map[p.projectId] = p.name;
        });
        setProjectsMap(map);
        setError(null);
      } catch (err: any) {
        setError('Failed to load requests');
      } finally {
        setLoading(false);
      }
    };
    fetchRequestsAndProjects();
  }, []);

  const handleUpdateStatus = async (id: string, status: 'approved' | 'rejected') => {
    try {
      await requestsAPI.updateStatus(id, status);
      setRequests((prev) => prev.map((r) => r._id === id ? { ...r, status } : r));
    } catch {
      alert('Failed to update status');
    }
  };

  const handleRejectClick = (id: string) => {
    setRejectingId(id);
    setRejectReason('');
    setRejectModalOpen(true);
  };

  const handleConfirmReject = async () => {
    if (!rejectingId || !rejectReason.trim()) return;
    try {
      await requestsAPI.updateStatus(rejectingId, 'rejected', rejectReason);
      setRequests((prev) => prev.map((r) => r._id === rejectingId ? { ...r, status: 'rejected', rejectReason } : r));
      setRejectModalOpen(false);
      setRejectingId(null);
      setRejectReason('');
    } catch {
      alert('Failed to reject request');
    }
  };

  if (loading) return <div>Loading requests...</div>;
  if (error) return <div className="text-red-500">{error}</div>;
  if (requests.length === 0) return <div>No requests found.</div>;

  // Split requests
  const managerRequests = requests.filter(r => r.managerId);
  const associateRequests = requests.filter(r => r.associateId);

  return (
    <div>
      <h2 className="text-xl font-bold mb-4">Manager Requests</h2>
      <div className="overflow-x-auto mb-8">
        <table className="min-w-full bg-white rounded shadow">
          <thead>
            <tr>
              <th className="px-4 py-2">Project</th>
              <th className="px-4 py-2">Manager</th>
              <th className="px-4 py-2">Client</th>
              <th className="px-4 py-2">Reason</th>
              <th className="px-4 py-2">Status</th>
              <th className="px-4 py-2">Created</th>
              <th className="px-4 py-2">Action</th>
              <th className="px-4 py-2">Reject Reason</th>
            </tr>
          </thead>
          <tbody>
            {managerRequests.map((req) => (
              <tr key={req._id}>
                <td className="border px-4 py-2">{projectsMap[req.projectId] || req.projectId}</td>
                <td className="border px-4 py-2">{req.managerName}</td>
                <td className="border px-4 py-2">{req.client}</td>
                <td className="border px-4 py-2">{req.reason}</td>
                <td className="border px-4 py-2">{req.status}</td>
                <td className="border px-4 py-2">{new Date(req.createdAt).toLocaleString()}</td>
                <td className="border px-4 py-2">
                  {req.status === 'pending' ? (
                    <>
                      <button
                        className="bg-green-500 text-white px-3 py-1 rounded mr-2 min-w-[80px]"
                        onClick={() => handleUpdateStatus(req._id, 'approved')}
                      >
                        Approve
                      </button>
                      <button className="bg-red-500 text-white px-3 py-1 rounded min-w-[80px]" onClick={() => handleRejectClick(req._id)}>Reject</button>
                    </>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </td>
                <td className="border px-4 py-2">{req.rejectReason || (req.status === 'rejected' ? 'No reason provided' : '')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h2 className="text-xl font-bold mb-4">Associate Requests</h2>
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white rounded shadow">
          <thead>
            <tr>
              <th className="px-4 py-2">Project</th>
              <th className="px-4 py-2">Associate</th>
              <th className="px-4 py-2">Reason</th>
              <th className="px-4 py-2">Status</th>
              <th className="px-4 py-2">Created</th>
              <th className="px-4 py-2">Action</th>
              <th className="px-4 py-2">Reject Reason</th>
            </tr>
          </thead>
          <tbody>
            {associateRequests.map((req) => (
              <tr key={req._id}>
                <td className="border px-4 py-2">{projectsMap[req.projectId] || req.projectId}</td>
                <td className="border px-4 py-2">{req.associateName}</td>
                <td className="border px-4 py-2">{req.reason}</td>
                <td className="border px-4 py-2">{req.status}</td>
                <td className="border px-4 py-2">{new Date(req.createdAt).toLocaleString()}</td>
                <td className="border px-4 py-2">
                  {req.status === 'pending' ? (
                    <>
                      <button
                        className="bg-green-500 text-white px-3 py-1 rounded mr-2 min-w-[80px]"
                        onClick={() => handleUpdateStatus(req._id, 'approved')}
                      >
                        Approve
                      </button>
                      <button className="bg-red-500 text-white px-3 py-1 rounded min-w-[80px]" onClick={() => handleRejectClick(req._id)}>Reject</button>
                    </>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </td>
                <td className="border px-4 py-2">{req.rejectReason || (req.status === 'rejected' ? 'No reason provided' : '')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {rejectModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
          <div className="bg-white p-6 rounded shadow-lg w-full max-w-md">
            <h3 className="text-lg font-bold mb-2">Reject Request</h3>
            <textarea
              className="w-full border rounded p-2 mb-4"
              rows={3}
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
              placeholder="Enter reason for rejection"
            />
            <div className="flex justify-end gap-2">
              <button className="px-4 py-1 rounded bg-gray-300" onClick={() => setRejectModalOpen(false)}>Cancel</button>
              <button className="px-4 py-1 rounded bg-red-500 text-white" onClick={handleConfirmReject} disabled={!rejectReason.trim()}>Reject</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const FinanceTabs = () => {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('dashboard');

  useEffect(() => {
    if (location.state && location.state.tab) {
      setActiveTab(location.state.tab);
    }
  }, [location.state]);
  return (
    <div className="p-8">
      {/* Summary Cards - always visible */}
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-gray-800 mb-2">Finance</h1>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white rounded-lg shadow p-6 flex flex-row items-center gap-4">
            <div className="bg-blue-50 rounded-full p-3"><span className="text-xl">💰</span></div>
            <div>
              <div className="text-gray-500 text-sm">Total Revenue</div>
              <div className="text-2xl font-bold mt-1">$24,500</div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6 flex flex-row items-center gap-4">
            <div className="bg-blue-50 rounded-full p-3"><span className="text-xl">📄</span></div>
            <div>
              <div className="text-gray-500 text-sm">Pending Invoices</div>
              <div className="text-2xl font-bold mt-1">5</div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6 flex flex-row items-center gap-4">
            <div className="bg-green-50 rounded-full p-3"><span className="text-xl">✅</span></div>
            <div>
              <div className="text-gray-500 text-sm">Paid Invoices</div>
              <div className="text-2xl font-bold mt-1">12</div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6 flex flex-row items-center gap-4">
            <div className="bg-purple-50 rounded-full p-3"><span className="text-xl">💸</span></div>
            <div>
              <div className="text-gray-500 text-sm">Total Expenses</div>
              <div className="text-2xl font-bold mt-1">$8,200</div>
            </div>
          </div>
        </div>
      </div>
      {/* Tabs - now below summary cards */}
      <div className="mb-8">
        <div className="flex space-x-2 border-b pb-2">
          {tabList.map(tab => (
            <button
              key={tab.key}
              className={`px-4 py-2 font-medium ${activeTab === tab.key ? 'text-blue-700 border-b-2 border-blue-700' : 'text-gray-500 hover:text-blue-700'}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>
      <TabContent activeTab={activeTab} />
    </div>
  );
};

export default FinanceTabs;
